import { useState } from 'react';

const trustItems = [
  { icon: 'ri-star-fill', label: '5-Star Studio Experience' },
  { icon: 'ri-group-line', label: 'All Ages Welcome' },
  { icon: 'ri-cake-line', label: 'Parties & Private Events' },
  { icon: 'ri-brush-line', label: 'Art Classes Year-Round' },
  { icon: 'ri-map-pin-2-line', label: 'Nazareth, PA & Lehigh Valley' },
];

const nearbyAreas = [
  { city: 'Nazareth', distance: 'Home Location', primary: true },
  { city: 'Bath', distance: '~4 miles' },
  { city: 'Stockertown', distance: '~5 miles' },
  { city: 'Bethlehem', distance: '~10 miles' },
  { city: 'Easton', distance: '~12 miles' },
  { city: 'Allentown', distance: '~16 miles' },
  { city: 'Northampton', distance: '~8 miles' },
  { city: 'Wind Gap', distance: '~7 miles' },
];

const steps = [
  { number: '01', title: 'Choose Your Experience', description: 'Browse available art classes and party bookings on the Art Den Studio calendar.', icon: 'ri-search-eye-line' },
  { number: '02', title: 'Reserve Your Spot', description: 'Book online or call us to secure your date and session.', icon: 'ri-calendar-check-line' },
  { number: '03', title: 'Visit Our Studio', description: 'Find us at 37 E Belvidere Street, Nazareth, PA — easy to reach from across the Lehigh Valley.', icon: 'ri-map-pin-2-line' },
  { number: '04', title: 'Create & Celebrate', description: 'Walk in, create, and leave with something you\'re proud of.', icon: 'ri-heart-3-line' },
];

const faqs = [
  { question: 'Where is Art Den Studio located?', answer: 'Art Den Studio is located at 37 E Belvidere Street in Nazareth, PA 18064. The studio is centrally positioned in Northampton County and easily accessible from Bethlehem, Easton, Allentown, and communities throughout the Lehigh Valley.' },
  { question: 'Who offers interior design services in Nazareth, PA?', answer: 'Art Den Studio in Nazareth, PA offers art classes, birthday parties, private events, and creative experiences for guests of all ages. Sessions run year-round and can be booked online. The studio is located at 37 E Belvidere Street and serves the greater Lehigh Valley community.' },
  { question: 'What are the hours at Art Den Studio?', answer: 'Art Den Studio\'s schedule is based on its monthly calendar of events, classes, and private bookings. Hours vary by session. Please check the calendar at artdenstudio.com for upcoming availability, or contact the studio at 610-340-0314 for questions about private events and scheduling.' },
  { question: 'Is there parking near Art Den Studio in Nazareth?', answer: 'Yes, the studio is centrally located in Nazareth, PA with local parking available near 37 E Belvidere Street. The downtown Nazareth area is accessible and easy to navigate. Contact the studio if you have any questions about getting there.' },
];

const LocationPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <main>
      {/* 1. HERO */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden" style={{ background: '#080808' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-artden-dark via-artden-dark/90 to-artden-dark/80" />
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle, #d4693a 1px, transparent 1px)', backgroundSize: '48px 48px' }}
        />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pb-16 md:pb-24 pt-40">
          <div className="inline-flex items-center gap-2 bg-artden-coral/20 border border-artden-coral/40 text-artden-coral px-4 py-1.5 rounded-full text-xs font-outfit font-semibold tracking-widest uppercase mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-2-line"></i></span>
            Nazareth, PA · Lehigh Valley
          </div>
          <h1 className="font-cormorant text-5xl md:text-7xl font-light text-white leading-tight mb-6 max-w-3xl">
            Art Classes in<br />
            <em className="font-medium text-artden-coral">Nazareth, PA</em>
          </h1>
          <p className="font-outfit text-white/70 text-lg max-w-2xl mb-8 leading-relaxed">
            Art Den Studio is Nazareth's premier creative studio — offering art classes, birthday parties, and private events for all ages. Centrally located in the heart of the Lehigh Valley.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://artdenstudio.com/calendar/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap"
            >
              Book a Class or Party
            </a>
            <a
              href="https://maps.google.com/?q=37+E+Belvidere+Street+Nazareth+PA+18064"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/30 text-white hover:border-artden-coral hover:text-artden-coral font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-navigation-line"></i></span>
              Get Directions
            </a>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-artden-dark to-transparent" />
      </section>

      {/* 2. TRUST BAR */}
      <section className="py-5 border-b border-white/5" style={{ background: '#0e0e0e' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {trustItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 text-white/70">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-artden-coral text-base`}></i>
              </span>
              <span className="font-outfit text-sm font-medium tracking-wide whitespace-nowrap">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 3. SERVICES OVERVIEW */}
      <section className="bg-artden-light py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Location & Services</p>
              <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-artden-dark leading-tight mb-6">
                What We Offer in<br /><em className="font-light">Nazareth, PA</em>
              </h2>
              <p className="font-outfit text-artden-muted text-base leading-relaxed mb-8">
                Art Den Studio in Nazareth offers a full range of creative experiences — from drop-in art classes to fully hosted birthday parties and private events. All sessions are guided by Kelsey with all supplies included.
              </p>
              <div className="space-y-4">
                {[
                  { icon: 'ri-brush-2-line', title: 'Art Classes', desc: 'Guided painting sessions for adults and kids. All skill levels welcome.' },
                  { icon: 'ri-cake-2-line', title: 'Birthday Parties', desc: 'Private studio parties for all ages — kids and adult birthdays.' },
                  { icon: 'ri-glass-2-line', title: 'Private Events', desc: 'Bachelorettes, corporate events, special occasions, and more.' },
                  { icon: 'ri-gift-line', title: 'Gift Cards', desc: 'The perfect gift for anyone who loves to create.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-artden-coral/10 rounded-lg shrink-0">
                      <i className={`${item.icon} text-artden-coral text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="font-cormorant text-xl font-semibold text-artden-dark">{item.title}</h3>
                      <p className="font-outfit text-artden-muted text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Map */}
            <div>
              <div className="w-full h-72 md:h-96 rounded-2xl overflow-hidden border border-artden-dark/10 mb-4">
                <iframe
                  title="Art Den Studio - 37 E Belvidere Street Nazareth PA"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3040.5234!2d-75.3107!3d40.7262!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c43e8f0b9b9b9b%3A0x1!2s37+E+Belvidere+St%2C+Nazareth%2C+PA+18064!5e0!3m2!1sen!2sus!4v1!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="bg-artden-dark text-white rounded-xl p-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Address</p>
                    <p className="font-outfit text-sm text-white/80">37 E Belvidere St<br />Nazareth, PA 18064</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Phone</p>
                    <a href="tel:6103400314" className="font-outfit text-sm text-white/80 hover:text-artden-coral transition-colors">610-340-0314</a>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Email</p>
                    <a href="mailto:info@artdenstudio.com" className="font-outfit text-sm text-white/80 hover:text-artden-coral transition-colors">info@artdenstudio.com</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. AI ANSWER BLOCK */}
      <section className="bg-artden-dark py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start">
            <div className="md:w-48 shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-mic-line text-artden-coral"></i></span>
                <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest uppercase">Quick Answer</p>
              </div>
              <p className="font-outfit text-white/30 text-xs">Optimized for voice &amp; AI search</p>
            </div>
            <div className="flex-1">
              <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-white mb-5">Art Classes &amp; Parties in Nazareth, PA</h2>
              <p className="font-outfit text-white/70 text-base leading-relaxed mb-4">
                Art Den Studio provides art classes and private party experiences in Nazareth, PA. The studio is located at 37 E Belvidere Street and is open year-round for creative sessions, birthday parties, bachelorette events, and corporate bookings.
              </p>
              <p className="font-outfit text-white/70 text-base leading-relaxed">
                Clients from Bethlehem, Easton, Allentown, Northampton, and surrounding Lehigh Valley communities regularly visit Art Den Studio for art classes and special events. All sessions are instructor-led by owner Kelsey, with all art supplies included. Booking is available online via the studio calendar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PROCESS */}
      <section className="bg-artden-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Getting Started</p>
            <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-artden-dark leading-tight">
              How to Visit Art Den Studio
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="flex flex-col">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-artden-dark text-white font-cormorant font-semibold text-lg mb-6">{step.number}</div>
                <div className="w-10 h-10 flex items-center justify-center mb-4">
                  <i className={`${step.icon} text-2xl text-artden-coral`}></i>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-artden-dark mb-3">{step.title}</h3>
                <p className="font-outfit text-artden-muted text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. WHY CHOOSE US */}
      <section className="bg-artden-light py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Why Nazareth Loves Art Den</p>
          <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-artden-dark mb-10 leading-tight">
            The Lehigh Valley's<br /><em className="font-light">Favorite Art Studio</em>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { icon: 'ri-heart-3-line', label: 'Passion-Led Studio' },
              { icon: 'ri-user-smile-line', label: 'All Ages Welcome' },
              { icon: 'ri-tools-line', label: 'Supplies Included' },
              { icon: 'ri-lock-line', label: 'Private Events Available' },
            ].map((item, i) => (
              <div key={i} className="bg-artden-cream rounded-xl p-5 flex flex-col items-center text-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-artden-coral/10 rounded-full">
                  <i className={`${item.icon} text-artden-coral text-xl`}></i>
                </div>
                <p className="font-outfit text-artden-dark text-sm font-semibold">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. EXPANDED SERVICE AREAS */}
      <section className="bg-artden-cream py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-3">Nearby Communities</p>
            <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-artden-dark mb-4">
              Serving All of Northampton County<br />&amp; the Lehigh Valley
            </h2>
            <p className="font-outfit text-artden-muted text-sm max-w-xl mx-auto">
              Whether you're coming from down the street or across the valley — Art Den Studio is worth the trip.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {nearbyAreas.map((area, i) => (
              <div
                key={i}
                className={`rounded-xl p-4 text-center border ${
                  area.primary
                    ? 'bg-artden-coral text-white border-artden-coral'
                    : 'bg-white border-artden-dark/10'
                }`}
              >
                <p className={`font-cormorant text-lg font-semibold ${area.primary ? 'text-white' : 'text-artden-dark'}`}>{area.city}</p>
                <p className={`font-outfit text-xs mt-0.5 ${area.primary ? 'text-white/80' : 'text-artden-muted'}`}>{area.distance}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. TESTIMONIALS */}
      <section className="bg-artden-light py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Local Reviews</p>
            <h2 className="font-cormorant text-4xl font-medium text-artden-dark">Guests from the Lehigh Valley</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { name: 'Megan H.', location: 'Bethlehem, PA', text: 'The drive from Bethlehem is absolutely worth it. Art Den Studio is such a special place — creative, welcoming, and genuinely fun. We make it a regular girls\' night.', type: 'Art Class' },
              { name: 'Paul D.', location: 'Easton, PA', text: 'Came in from Easton with my kids for a weekend class. They were talking about it for weeks. Kelsey is an incredible teacher and the studio space is beautiful.', type: 'Kids Class' },
              { name: 'Stephanie R.', location: 'Allentown, PA', text: 'Totally worth the trip from Allentown. This is one of those places that doesn\'t exist anywhere else — the energy, the instruction, and the results are all top tier.', type: 'Birthday Party' },
            ].map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-artden-cream">
                <span className="inline-block bg-artden-coral/10 text-artden-coral text-xs font-semibold px-3 py-1 rounded-full mb-4">{t.type}</span>
                <div className="flex gap-0.5 mb-4">
                  {[1,2,3,4,5].map(s => <span key={s} className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-artden-gold text-sm"></i></span>)}
                </div>
                <p className="font-outfit text-artden-muted text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div>
                  <p className="font-outfit text-artden-dark text-sm font-semibold">{t.name}</p>
                  <p className="font-outfit text-artden-muted text-xs">{t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="bg-artden-dark py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">FAQ</p>
            <h2 className="font-cormorant text-4xl font-medium text-white">Location Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-xl overflow-hidden">
                <button className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <h3 className="font-outfit text-white text-sm md:text-base font-medium">{faq.question}</h3>
                  <span className="w-6 h-6 flex items-center justify-center shrink-0">
                    <i className={`text-artden-coral ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="font-outfit text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section className="bg-artden-light py-20 text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="font-cormorant text-4xl md:text-6xl font-medium text-artden-dark mb-5 leading-tight">
            Visit Us in<br /><em className="font-light text-artden-coral">Nazareth, PA</em>
          </h2>
          <p className="font-outfit text-artden-muted text-base mb-8">
            Art Den Studio · 37 E Belvidere Street, Nazareth, PA 18064 · 610-340-0314
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://artdenstudio.com/calendar/" target="_blank" rel="noopener noreferrer" className="bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-8 py-4 rounded-full whitespace-nowrap w-full sm:w-auto">
              Book a Class or Party
            </a>
            <a href="https://maps.google.com/?q=37+E+Belvidere+Street+Nazareth+PA+18064" target="_blank" rel="noopener noreferrer" className="bg-artden-dark hover:bg-artden-coral text-white font-semibold px-8 py-4 rounded-full whitespace-nowrap w-full sm:w-auto flex items-center justify-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-2-line"></i></span>
              Get Directions
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LocationPage;
